import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { OfertasContext } from '../context/ofertas-context';
import { capitalizeSentence } from '../helpers/ofertas-helper';
import { useOfertasTracker } from '../helpers/use-ofertas-tracker';
import {
	CarlineWithOfertas,
	FilterTypes,
	gamaTypes,
	MobileFilterOption,
	motorizacionTypes,
	Oferta,
} from '../services/ofertas-service/models/ofertas';

export const useOfertasFilter = () => {
	const { ofertasService } = useContext(OfertasContext);
	const { trackOfertasGenericFeatureAppLoad, trackOfertasVehiclePageLoad } = useOfertasTracker();

	const [filteredCarlines, setFilteredCarlines] = useState<CarlineWithOfertas[]>([]);
	const [selectedGamaFilter, setSelectedGamaFilter] = useState<string | null>(null);
	const [selectedMotorizacionFilter, setSelectedMotorizacionFilter] = useState<string | null>(null);
	const isFilterSelectedRef = useRef<boolean>(false);

	const COMBUSTIBLE_TO_FILTER: Record<string, FilterTypes> = {
		'Eléctrico': FilterTypes.ELECTRICOS,
		'Híbrido': FilterTypes.HIBRIDOS_ENCHUFABLES,
		'Híbrido enchufable': FilterTypes.HIBRIDOS_ENCHUFABLES,
		'Gasolina': FilterTypes.COMBUSTION,
		'Diésel': FilterTypes.COMBUSTION,
		'Gasolina mild Hybrid (etiqueta ECO)': FilterTypes.COMBUSTION,
	};

	// Helper para generar una clave única para cada combinación de filtros
	const getFilterKey = (gama: string | null, motorizacion: string | null) =>
		`${gama ?? '__ALL_GAMA__'}|${motorizacion ?? '__ALL_MOT__'}`;

	const getFilteredCarlines = useCallback(
		(gama: string | null, motorizacion: string | null): CarlineWithOfertas[] =>
			ofertasService.filterOfertas(gama, motorizacion),
		[ofertasService],
	);

	// Control de click en filtro
	// -> evita que se vuelva a filtrar si el filtro ya está activo
	// -> dispara el tracking
	const handleFilteredCarlines = useCallback(
		(selectedButton: string) => {
			if (selectedGamaFilter === selectedButton || selectedMotorizacionFilter === selectedButton) {
				return;
			}
			isFilterSelectedRef.current = true;
		},
		[selectedGamaFilter, selectedMotorizacionFilter],
	);

	// Carlines disponibles
	const allCarlines = useMemo(() => getFilteredCarlines(null, null), [getFilteredCarlines]);

	// Calculo del número de ofertas disponibles para cada filtro y combinación de filtros
	const filterStats = useMemo(() => {
		const gamaCounts = new Map<string, number>();
		const motorizacionCounts = new Map<string, number>();
		const combinationCounts = new Map<string, number>();
		let totalOfertas = 0;

		const getMotorizacionType = (oferta: Oferta): string | null => {
			const combustible = oferta.configuracion.motorizacion.combustible;
			return COMBUSTIBLE_TO_FILTER[combustible] ?? null;
		};

		allCarlines.forEach((carline) => {
			carline.ofertas.forEach((oferta) => {
				totalOfertas += 1;

				const gama = oferta.gama;
				const motorizacion = getMotorizacionType(oferta);

				if (gama) {
					gamaCounts.set(gama, (gamaCounts.get(gama) ?? 0) + 1);
				}

				if (motorizacion) {
					motorizacionCounts.set(motorizacion, (motorizacionCounts.get(motorizacion) ?? 0) + 1);
				}

				if (gama && motorizacion) {
					const combinationKey = getFilterKey(gama, motorizacion);
					combinationCounts.set(combinationKey, (combinationCounts.get(combinationKey) ?? 0) + 1);
				}
			});
		});

		return {
			totalOfertas,
			gamaCounts,
			motorizacionCounts,
			combinationCounts,
		};
	}, [allCarlines]);

	// Gamas disponibles
	const gamaFilterTypes: string[] = useMemo(() => {
		return gamaTypes.filter((filterType) => (filterStats.gamaCounts.get(filterType) ?? 0) > 0);
	}, [filterStats]);

	// Motorizaciones disponibles
	const motorizacionFilterTypes: string[] = useMemo(() => {
		return motorizacionTypes.filter(
			(filterType) => filterType !== FilterTypes.TODOS && (filterStats.motorizacionCounts.get(filterType) ?? 0) > 0,
		);
	}, [filterStats]);

	// Función para verificar si una combinación de filtros está disponible
	const isCombinationAvailable = useCallback(
		(gama: string | null, motorizacion: string | null): boolean => {
			if (!gama && !motorizacion) {
				return filterStats.totalOfertas > 0;
			}

			if (gama && !motorizacion) {
				return (filterStats.gamaCounts.get(gama) ?? 0) > 0;
			}

			if (!gama && motorizacion) {
				return (filterStats.motorizacionCounts.get(motorizacion) ?? 0) > 0;
			}

			return (filterStats.combinationCounts.get(getFilterKey(gama, motorizacion)) ?? 0) > 0;
		},
		[filterStats],
	);

	// Opciones de filtro para mobile
	const mobileFilterOptions: MobileFilterOption[] = useMemo(() => {
		const filters: MobileFilterOption[] = [];

		const gamaItems = Object.fromEntries(
			gamaFilterTypes
				.filter((g) => isCombinationAvailable(g, selectedMotorizacionFilter))
				.map((g) => [g, capitalizeSentence(g)]),
		);
		if (Object.keys(gamaItems).length > 1) filters.push({ groupTitle: 'Gama', items: gamaItems });

		const motorizacionItems = Object.fromEntries(
			motorizacionFilterTypes.filter((m) => isCombinationAvailable(selectedGamaFilter, m)).map((m) => [m, m]),
		);
		if (Object.keys(motorizacionItems).length > 1)
			filters.push({ groupTitle: 'Motorización', items: motorizacionItems });

		return filters;
	}, [
		gamaFilterTypes,
		motorizacionFilterTypes,
		selectedGamaFilter,
		selectedMotorizacionFilter,
		isCombinationAvailable,
	]);

	// useEffect para actualizar los carlines filtrados cuando cambian los filtros seleccionados
	useEffect(() => {
		const carlines = getFilteredCarlines(selectedGamaFilter, selectedMotorizacionFilter);
		setFilteredCarlines(carlines);

		if (carlines.length > 0) {
			const carline = carlines[0];

			if (!isFilterSelectedRef.current) {
				trackOfertasGenericFeatureAppLoad(carline);
			} else {
				trackOfertasVehiclePageLoad(carline);
				isFilterSelectedRef.current = false;
			}
		}
	}, [
		selectedGamaFilter,
		selectedMotorizacionFilter,
		getFilteredCarlines,
		trackOfertasGenericFeatureAppLoad,
		trackOfertasVehiclePageLoad,
	]);

	// Función que devuelve el número de ofertas basado en los filtros seleccionados
	const calculateOfertasNumberOnFilter = useCallback(
		(gama: string | null, motorizacion: string | null): number => {
			if (!gama && !motorizacion) {
				return filterStats.totalOfertas;
			}

			if (gama && !motorizacion) {
				return filterStats.gamaCounts.get(gama) ?? 0;
			}

			if (!gama && motorizacion) {
				return filterStats.motorizacionCounts.get(motorizacion) ?? 0;
			}

			return filterStats.combinationCounts.get(getFilterKey(gama, motorizacion)) ?? 0;
		},
		[filterStats],
	);

	return {
		filteredCarlines,
		handleFilteredCarlines,

		selectedGamaFilter,
		setSelectedGamaFilter,
		selectedMotorizacionFilter,
		setSelectedMotorizacionFilter,

		gamaFilterTypes,
		motorizacionFilterTypes,
		mobileFilterOptions,
		isCombinationAvailable,

		calculateOfertasNumberOnFilter,
	};
};
